import type { paths } from "@/api-schema";
import { PathsV1RelocationOrderUuidGetParametersQueryExtend } from "@/api-schema-enums";
import { useRelocationOrderByUuidQuery } from "@/common/api/relocation-order/use-relocation-order-by-uuid.query";
import { useRelocationOrderUpdateMutation } from "@/common/api/relocation-order/use-relocation-order-update.mutation";
import { dateToInputString, parseApiDateOnly } from "@/common/helpers/date-input";
import { formatDateToApi } from "@/common/helpers/format-date";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import {
  addressToAddressFormValues,
  isAddressFormEmpty,
} from "@/components/address-form-fields/_helpers/address-form-values";
import { addressFormValuesToAddressDto } from "@/components/address-form-fields/_helpers/address-form-values-to-address-dto";
import { addressFormDefaultValues, addressFormSchema } from "@/components/address-form-fields/address-form-schema";
import { CenteredLayout, CenteredLayoutVariant } from "@/components/layouts/centered-layout";
import { UuidExistenceWrapper } from "@/components/uuid-existence-wrapper/uuid-existence-wrapper";
import {
  RelocationOrderAdminForm,
  RelocationOrderAdminFormSkeleton,
} from "@/modules/fit/internal/relocation-order/admin/_components/relocation-order-admin-form";
import { sanitizeRelocationOrderValues } from "@/modules/fit/internal/relocation-order/admin/_helpers/sanitize-relocation-order-values";
import { useRelocationOrderAdminFormState } from "@/modules/fit/internal/relocation-order/admin/_hooks/use-relocation-order-admin-form-state";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "wouter";
import { navigate } from "wouter/use-browser-location";
import {
  buildRelocationOrderAdminFormSchema,
  RelocationOrderAdminFormSchemaType,
} from "@/modules/fit/internal/relocation-order/admin/_components/relocation-order-admin-form-fields";

interface AdminUpdateViewParams {
  uuid: string;
}

type RelocationOrderResponse =
  paths["/v1/relocation-order/{uuid}"]["get"]["responses"][200]["content"]["application/json"];

export function AdminUpdateView() {
  const { t } = useTranslation(["common", "fit"]);
  const params = useParams<AdminUpdateViewParams>();
  const uuid = params.uuid;

  return (
    <UuidExistenceWrapper<
      paths["/v1/relocation-order/{uuid}"]["get"]["parameters"]["query"],
      paths["/v1/relocation-order/{uuid}"]["get"]["responses"][200]["content"]["application/json"]
    >
      uuid={uuid}
      query={useRelocationOrderByUuidQuery}
      notFoundProps={{
        title: t("fit:RelocationOrderNotFound"),
        message: t("fit:RelocationOrderDoesNotExist"),
        description: t("fit:CheckRelocationOrderIdOrContactSupport"),
        backTo: "~/fit/relocation-order",
        backToLabel: t("fit:BackToOverview"),
      }}
    >
      <AdminUpdateContent uuid={uuid} />
    </UuidExistenceWrapper>
  );
}

function AdminUpdateContent({ uuid }: { uuid: string }) {
  const { t } = useTranslation(["common", "fit"]);
  const pageTitle = t("fit:AdminUpdate");
  const pageSubtitle = t("common:ReviewAndSaveChangesDescription");
  const { data: relocationOrderData, isPending: isRelocationOrderLoading } = useRelocationOrderByUuidQuery(uuid, {
    parameters: {
      extend: [
        PathsV1RelocationOrderUuidGetParametersQueryExtend.CurrentBranch,
        PathsV1RelocationOrderUuidGetParametersQueryExtend.CurrentAddress,
        PathsV1RelocationOrderUuidGetParametersQueryExtend.OriginBranch,
        PathsV1RelocationOrderUuidGetParametersQueryExtend.OriginAddress,
        PathsV1RelocationOrderUuidGetParametersQueryExtend.DestinationBranch,
        PathsV1RelocationOrderUuidGetParametersQueryExtend.DestinationAddress,
        PathsV1RelocationOrderUuidGetParametersQueryExtend.Vehicle,
        PathsV1RelocationOrderUuidGetParametersQueryExtend.Vehicle_ModelGeneration,
        PathsV1RelocationOrderUuidGetParametersQueryExtend.Vehicle_ModelGeneration_ModelFamily,
        PathsV1RelocationOrderUuidGetParametersQueryExtend.Vehicle_ModelGeneration_ModelFamily_Brand,
        PathsV1RelocationOrderUuidGetParametersQueryExtend.Vehicle_CurrentBranch,
        PathsV1RelocationOrderUuidGetParametersQueryExtend.Vehicle_CurrentBranch_Address,
        PathsV1RelocationOrderUuidGetParametersQueryExtend.Vehicle_CurrentBranch_Company,
        PathsV1RelocationOrderUuidGetParametersQueryExtend.Vehicle_CurrentBranch_Brands,
        PathsV1RelocationOrderUuidGetParametersQueryExtend.TransportCompany,
        PathsV1RelocationOrderUuidGetParametersQueryExtend.PickedUpBy,
        PathsV1RelocationOrderUuidGetParametersQueryExtend.OrderedBy,
      ],
    },
  });

  return (
    <CenteredLayout variant={CenteredLayoutVariant.Form} title={pageTitle} subTitle={pageSubtitle}>
      {isRelocationOrderLoading || !relocationOrderData ? (
        <RelocationOrderAdminFormSkeleton />
      ) : (
        <AdminUpdateForm relocationOrderData={relocationOrderData} />
      )}
    </CenteredLayout>
  );
}

function AdminUpdateForm({ relocationOrderData }: { relocationOrderData: RelocationOrderResponse }) {
  const { t } = useTranslation(["common", "fit"]);
  const { isPending: relocationOrderUpdateMutationIsPending, mutateAsync } = useRelocationOrderUpdateMutation();

  const form = useForm<RelocationOrderAdminFormSchemaType>({
    mode: "onChange",
    resolver: zodResolver(buildRelocationOrderAdminFormSchema()),
    defaultValues: {
      originBranch: relocationOrderData.originBranch?.uuid ?? "",
      originAddress: relocationOrderData.originAddress
        ? addressToAddressFormValues(relocationOrderData.originAddress)
        : { ...addressFormDefaultValues },
      currentBranch: relocationOrderData.currentBranch?.uuid ?? null,
      currentAddress: relocationOrderData.currentAddress
        ? addressToAddressFormValues(relocationOrderData.currentAddress)
        : { ...addressFormDefaultValues },
      destinationBranch: relocationOrderData.destinationBranch?.uuid ?? "",
      destinationAddress: relocationOrderData.destinationAddress
        ? addressToAddressFormValues(relocationOrderData.destinationAddress)
        : { ...addressFormDefaultValues },
      earliestPickupDate: dateToInputString(parseApiDateOnly(relocationOrderData.earliestPickupDate)),
      pickedUpBy: relocationOrderData.pickedUpBy?.uuid ?? null,
      orderedBy: relocationOrderData.orderedBy?.uuid ?? null,
      status: relocationOrderData.status,
      transportCompany: relocationOrderData.transportCompany?.uuid ?? null,
      transportMethod: relocationOrderData.transportMethod,
      transportReason: relocationOrderData.transportReason,
      vehicle: relocationOrderData.vehicle.uuid,
    },
  });

  const selectedVehicle = relocationOrderData.vehicle;

  const { options } = useRelocationOrderAdminFormState({
    form,
    vehicle: selectedVehicle,
    savedStatus: relocationOrderData.status,
  });

  const onSubmit = async (values: RelocationOrderAdminFormSchemaType) => {
    if (!selectedVehicle) {
      return;
    }

    const currentOriginBranchUuid = relocationOrderData.originBranch?.uuid ?? null;
    const currentCurrentBranchUuid = relocationOrderData.currentBranch?.uuid ?? null;
    const currentDestinationBranchUuid = relocationOrderData.destinationBranch?.uuid ?? null;
    const sanitized = sanitizeRelocationOrderValues(values);
    const normalizedOriginBranchUuid = sanitized.originBranch || null;
    const normalizedCurrentBranchUuid = sanitized.currentBranch || null;
    const normalizedDestinationBranchUuid = sanitized.destinationBranch || null;
    const hasOriginAddress = !isAddressFormEmpty(sanitized.originAddress);
    const hasCurrentAddress = !isAddressFormEmpty(sanitized.currentAddress);
    const hasDestinationAddress = !isAddressFormEmpty(sanitized.destinationAddress);
    const originAddressDirty = hasNestedDirtyFields(form.formState.dirtyFields.originAddress);
    const currentAddressDirty = hasNestedDirtyFields(form.formState.dirtyFields.currentAddress);
    const destinationAddressDirty = hasNestedDirtyFields(form.formState.dirtyFields.destinationAddress);
    const originBranchChanged = normalizedOriginBranchUuid !== currentOriginBranchUuid;
    const currentBranchChanged = normalizedCurrentBranchUuid !== currentCurrentBranchUuid;
    const destinationBranchChanged = normalizedDestinationBranchUuid !== currentDestinationBranchUuid;
    const parsedOriginAddress = hasOriginAddress ? addressFormSchema.parse(sanitized.originAddress) : undefined;
    const parsedCurrentAddress = hasCurrentAddress ? addressFormSchema.parse(sanitized.currentAddress) : undefined;
    const parsedDestinationAddress = hasDestinationAddress
      ? addressFormSchema.parse(sanitized.destinationAddress)
      : undefined;

    const originAddress = originAddressDirty
      ? hasOriginAddress
        ? addressFormValuesToAddressDto(parsedOriginAddress)
        : normalizedOriginBranchUuid || currentOriginBranchUuid
          ? null
          : undefined
      : originBranchChanged && normalizedOriginBranchUuid
        ? null
        : undefined;

    const currentAddress = currentAddressDirty
      ? hasCurrentAddress
        ? addressFormValuesToAddressDto(parsedCurrentAddress)
        : normalizedCurrentBranchUuid || currentCurrentBranchUuid
          ? null
          : undefined
      : currentBranchChanged && normalizedCurrentBranchUuid
        ? null
        : undefined;

    const destinationAddress = destinationAddressDirty
      ? hasDestinationAddress
        ? addressFormValuesToAddressDto(parsedDestinationAddress)
        : normalizedDestinationBranchUuid || currentDestinationBranchUuid
          ? null
          : undefined
      : destinationBranchChanged && normalizedDestinationBranchUuid
        ? null
        : undefined;

    const payload = {
      originBranch: originBranchChanged ? normalizedOriginBranchUuid : undefined,
      originAddress,
      currentBranch: currentBranchChanged ? normalizedCurrentBranchUuid : undefined,
      currentAddress,
      destinationBranch:
        normalizedDestinationBranchUuid !== currentDestinationBranchUuid ? normalizedDestinationBranchUuid : undefined,
      destinationAddress,
      earliestPickupDate:
        sanitized.earliestPickupDate !== formatDateToApi(parseApiDateOnly(relocationOrderData.earliestPickupDate)!)
          ? sanitized.earliestPickupDate
          : undefined,
      pickedUpBy: sanitized.pickedUpBy !== relocationOrderData.pickedUpBy?.uuid ? sanitized.pickedUpBy : undefined,
      orderedBy: sanitized.orderedBy !== relocationOrderData.orderedBy?.uuid ? sanitized.orderedBy : undefined,
      status: sanitized.status !== relocationOrderData.status ? sanitized.status : undefined,
      transportCompany:
        sanitized.transportCompany !== relocationOrderData.transportCompany?.uuid
          ? sanitized.transportCompany
          : undefined,
      transportMethod:
        sanitized.transportMethod !== relocationOrderData.transportMethod ? sanitized.transportMethod : undefined,
      transportReason:
        sanitized.transportReason !== relocationOrderData.transportReason ? sanitized.transportReason : undefined,
      uuid: relocationOrderData.uuid,
      vehicle: sanitized.vehicle !== relocationOrderData.vehicle.uuid ? sanitized.vehicle : undefined,
    };

    await toastMutationStatus({
      mutationFn: () => mutateAsync(payload),
      successMessage: t("fit:RelocationOrderWasEdited"),
      errorMessage: t("fit:ErrorEditingRelocationOrder"),
      customErrors: {
        409: null,
      },
    });

    navigate("/fit/relocation-order");
  };

  return (
    <RelocationOrderAdminForm
      cardTitle={t("common:RelocationOrder")}
      vehicle={selectedVehicle}
      relocationOrder={relocationOrderData}
      form={form}
      onSubmit={onSubmit}
      isLoading={relocationOrderUpdateMutationIsPending}
      options={options}
    />
  );
}

function hasNestedDirtyFields(value: unknown): boolean {
  if (!value) {
    return false;
  }

  if (typeof value === "boolean") {
    return value;
  }

  if (typeof value !== "object") {
    return false;
  }

  return Object.values(value).some((nestedValue) => hasNestedDirtyFields(nestedValue));
}
